/**
 * V82: 직원별 상세 휴가 사용 내역 모달
 *
 * 인사관리 > 휴가현황 > 직원명 클릭 시 표시.
 *
 * BE가 `amountLabel` / `sourceLabel` / `sortKey` 를 완성해서 내려주므로
 * FE는 순수 렌더링만 담당.
 */

import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { ModalOverlay } from '../../../shared/components/ModalOverlay';
import { useEmployeeLeaveUsageDetails, LeaveUsageDetailItem } from '../../../shared/hooks/useEmployeeLeaveUsageDetails';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  employeeId: string;
  employeeName: string;
  employeeNumber?: string;
}

const SOURCE_COLOR: Record<LeaveUsageDetailItem['source'], string> = {
  request: '#0ea5e9',
  import: '#64748b',
  manual_adjustment: '#f59e0b',
};

// V82.2: 대체휴무는 leaveTypeCode='compensatory'로 구분하여 별도 색상
const COMPENSATORY_COLOR = '#8b5cf6';

function resolveSourceColor(item: LeaveUsageDetailItem): string {
  if (item.source === 'request' && item.leaveTypeCode === 'compensatory') {
    return COMPENSATORY_COLOR;
  }
  return SOURCE_COLOR[item.source];
}

export function EmployeeLeaveUsageDetailModal({ isOpen, onClose, employeeId, employeeName, employeeNumber }: Props) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number | undefined>(currentYear);

  const { items, summary, isLoading, error } = useEmployeeLeaveUsageDetails(
    isOpen ? employeeId : null,
    selectedYear
  );

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    years.add(currentYear);
    years.add(currentYear - 1);
    years.add(currentYear - 2);
    return Array.from(years).sort((a, b) => b - a);
  }, [currentYear]);

  if (!isOpen) return null;

  return (
    <ModalOverlay
      onClose={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '8px',
          width: '90vw',
          maxWidth: '900px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-gray-200)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
              상세 휴가 사용 내역
            </h2>
            <div style={{ marginTop: '4px', fontSize: '13px', color: 'var(--color-gray-600)' }}>
              {employeeName}
              {employeeNumber ? ` (${employeeNumber})` : ''}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: 'var(--color-gray-600)',
            }}
            aria-label="닫기"
          >
            <X size={20} />
          </button>
        </div>

        {/* Filter + Summary */}
        <div
          style={{
            padding: '12px 20px',
            borderBottom: '1px solid var(--color-gray-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '13px', color: 'var(--color-gray-700)' }}>연도</label>
            <select
              value={selectedYear ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedYear(v === '' ? undefined : Number(v));
              }}
              style={{
                padding: '4px 8px',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '4px',
                fontSize: '13px',
              }}
            >
              <option value="">전체</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}년
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '12px', fontSize: '12px', flexWrap: 'wrap' }}>
            <SummaryChip label="승인" value={summary.totalApprovedDays} color="#0ea5e9" />
            <SummaryChip label="임포트" value={summary.totalImportedDays} color="#64748b" />
            <SummaryChip label="수정" value={summary.totalAdjustedDays} color="#f59e0b" signed />
            {/* V82.2: 대체휴무 합계 (BE 완성 라벨) */}
            <SummaryChipText label={summary.compensatoryLabel} color={COMPENSATORY_COLOR} />
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px' }}>
          {isLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
              조회 중...
            </div>
          ) : error ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-red-600)' }}>
              {error.message}
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
              조회된 휴가 사용 내역이 없습니다.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: 'var(--color-gray-50)', borderBottom: '2px solid var(--color-gray-200)' }}>
                  <Th>사용일자</Th>
                  <Th>휴가종류</Th>
                  <Th>사용단위</Th>
                  <Th align="right">사용량</Th>
                  <Th>출처</Th>
                  <Th>비고</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const color = resolveSourceColor(it);
                  return (
                    <tr key={it.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                      <Td>{it.displayDate}</Td>
                      <Td>{it.leaveTypeName || it.leaveTypeCode}</Td>
                      <Td>{formatUnit(it.unit)}</Td>
                      <Td align="right" style={{ fontWeight: 600, color: it.amount < 0 ? 'var(--color-red-600)' : undefined }}>
                        {it.amountLabel}
                      </Td>
                      <Td>
                        <span
                          style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            background: color + '22',
                            color,
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {it.sourceLabel}
                        </span>
                      </Td>
                      <Td style={{ color: 'var(--color-gray-600)', fontSize: '12px', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.reason || ''}>
                        {it.reason || '-'}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--color-gray-200)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '6px 16px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            닫기
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) {
  return (
    <th
      style={{
        padding: '8px 8px',
        textAlign: align || 'left',
        fontSize: '12px',
        fontWeight: 600,
        color: 'var(--color-gray-700)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align, style, title }: { children: React.ReactNode; align?: 'left' | 'right' | 'center'; style?: React.CSSProperties; title?: string }) {
  return (
    <td
      title={title}
      style={{
        padding: '8px 8px',
        textAlign: align || 'left',
        verticalAlign: 'middle',
        ...style,
      }}
    >
      {children}
    </td>
  );
}

function SummaryChip({ label, value, color, signed }: { label: string; value: number; color: string; signed?: boolean }) {
  const display = signed && value > 0 ? `+${value}일` : signed && value < 0 ? `${value}일` : `${value}일`;
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '12px',
        background: color + '1a',
        color,
        fontWeight: 500,
      }}
    >
      <span>{label}</span>
      <span>{display}</span>
    </div>
  );
}

function SummaryChipText({ label, color }: { label: string; color: string }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: '12px',
        background: color + '1a',
        color,
        fontWeight: 500,
      }}
    >
      {label}
    </div>
  );
}

function formatUnit(unit: LeaveUsageDetailItem['unit']): string {
  switch (unit) {
    case 'fullDay': return '전일';
    case 'halfDay': return '반차';
    case 'morningHalf': return '오전반차';
    case 'afternoonHalf': return '오후반차';
    case 'hourly': return '시간';
    case 'aggregate': return '합산';
    default: return '-';
  }
}
